// Small building blocks for the scheduling pages (BookMe's Layout helpers), plus the branded frame for the public
// booking and manage pages: the company's logo and colour on top, "Scheduling by eBookMe" underneath.
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { config } from "../../lib/auth";
import { CompanyMark, Wordmark } from "../../components/icons";

export function Loading() {
  return (
    <div className="empty">
      <span className="spin" />
    </div>
  );
}

export function ErrorBox({ error }: { error?: string }) {
  return error ? (
    <div className="alert" role="alert">
      {error}
    </div>
  ) : null;
}

export function Card({ children }: { children: ReactNode }) {
  return <section className="card">{children}</section>;
}

export interface TenantBrand {
  slug: string;
  name: string;
  color: string;
  logo?: string;
}

let tenant: Promise<TenantBrand | null> | null = null;
/** The company this host belongs to (name, colour, logo). Cached for the page's life. */
export function loadTenant() {
  tenant ??= fetch("/api/public/tenant")
    .then((r) => (r.ok ? (r.json() as Promise<TenantBrand>) : null))
    .catch(() => null);
  return tenant;
}

const HEX = /^#[0-9a-f]{6}$/i;

/** Public pages (/b/:slug, /manage/:id): no account, the company's branding. */
export function PublicFrame({ children }: { children: ReactNode }) {
  const [brand, setBrand] = useState<TenantBrand | null | undefined>(undefined);
  useEffect(() => {
    void loadTenant().then(setBrand);
  }, []);
  const landing = config()?.landingUrl ?? "https://ebookme.app";
  const style = brand && HEX.test(brand.color) ? ({ "--company": brand.color } as CSSProperties) : undefined;
  return (
    <div className="bk public" style={style}>
      <div className="brand-mark">{brand && <CompanyMark name={brand.name} logo={brand.logo} height={32} />}</div>
      {children}
      <div className="footer">
        <a href={landing} aria-label="eBookMe" style={{ textDecoration: "none" }}>
          <span style={{ verticalAlign: "middle" }}>Scheduling by </span>
          <Wordmark height={11} className="align-middle" />
        </a>
        <div style={{ marginTop: 6 }}>
          <a href={`${landing}/privacy`}>Privacy</a> · <a href={`${landing}/terms`}>Terms</a>
        </div>
      </div>
    </div>
  );
}
